import { ENDPOINTS } from 'shared/constants/ENDPOINTS';
import { useAxios } from 'shared/hooks/useAxios';
import {
  IFilterVariables,
  IMediaTagsResponsePayload,
  IProfileResponsePayload,
} from 'shared/interfaces/IProfileCalendar';

import {
  IGuardianAttachPayload,
  ISearchTalentResponsePayload,
  ISenderInfoResponsePayload,
  ISpecificTalentResponsePayload,
  ITalentAgencyResponsePayload,
  ITalentAgencyUpdateRequestPayload,
  ITalentBiographyResponsePayload,
  ITalentCalendarEventAggregatesPayload,
  ITalentCalendarEventAggregatesResponse,
  ITalentCalendarEventPayload,
  ITalentCalendarEventsPayload,
  ITalentCalendarEventsResponse,
  ITalentCreatePayload,
  ITalentCreateResponse,
  ITalentGeneralResponsePayload,
  ITalentInvitePayload,
  ITalentListingResponse,
  ITalentListPayload,
  ITalentListRequestPayload,
  ITalentListResponsePayload,
  ITalentMeResponsePayload,
  ITalentPlaylistPayload,
  ITalentPlaylistResponse,
  ITalentResumeResponsePayload,
  ITalentSkillResponsePayload,
  ITalentStatisticsResponsePayload,
  ITalentUpdatePayload,
  TSearchTalentPayload,
} from 'shared/interfaces/ITalentCalendar';

import {
  BIOGRAPHY_DATA,
  GET_TALENT_PROFILES,
  QS_QUERIES,
  RESUME_DATA,
  SEARCH_TALENT,
  SIZE_CARD_DATA,
  SKILLS_DATA,
  SOCIAL_DATA,
  SPECIFIC_TALENT,
  SPECIFIC_TALENT_EMAIL,
  SPECIFIC_TALENT_GENERAL_APPEARANCE,
  SPECIFIC_TALENT_PROFESSIONS,
  SPECIFIC_TALENT_SIZE_CARD,
  SPECIFIC_TALENT_WORKSHEET_SIZE_CARD,
  TALENT_CALENDAR_EVENTS,
  TALENT_LIST,
  TALENT_LIST_CARD,
  TALENT_PLAYLIST,
  TOTAL_TALENT_LIST,
} from 'shared/queries/talentCalendar.queries';

import { authToken } from 'shared/utils/authToken';

const { GET, PATCH, POST, DELETE } = useAxios();
export const talentDao = () => {
  const { getAuthToken } = authToken();
  const updateTalent = async (payload: ITalentUpdatePayload) => {
    const response = await PATCH<ITalentAgencyResponsePayload>({
      url: `${ENDPOINTS.TALENTS}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  const getMediaTags = async (searchString: string) => {
    const response = await GET<IMediaTagsResponsePayload>({
      url: `${ENDPOINTS.MEDIA_TAGS}?search=${searchString}&tags=media_tags`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  const searchTalent = async (searchObject: TSearchTalentPayload) => {
    const response = await POST<ISearchTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: SEARCH_TALENT,
        variables: {
          ...searchObject,
        },
      },
    });

    return response.data;
  };

  const specificTalent = async (talentId: string) => {
    const response = await POST<ISpecificTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: SPECIFIC_TALENT,
        variables: {
          id: talentId,
        },
      },
    });

    return response.data;
  };

  const getTalentPlaylist = async (parentProfileId: string) => {
    const response = await POST<ITalentPlaylistResponse>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: TALENT_PLAYLIST,
        variables: {
          parent_profile_id: parentProfileId,
        },
      },
    });

    return response.data;
  };

  const specificTalentGeneralAppearance = async (talentId: string) => {
    const response = await POST<ISpecificTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: SPECIFIC_TALENT_GENERAL_APPEARANCE,
        variables: {
          id: talentId,
        },
      },
    });

    return response.data;
  };

  const specificTalentSizeCard = async (talentId: string) => {
    const response = await POST<ISpecificTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: SPECIFIC_TALENT_SIZE_CARD,
        variables: {
          id: talentId,
        },
      },
    });

    return response.data;
  };

  const specificTalentResume = async (talentId: string) => {
    const response = await POST<ISpecificTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: RESUME_DATA,
        variables: {
          id: talentId,
        },
      },
    });

    return response.data;
  };

  const specificTalentBiography = async (talentId: string) => {
    const response = await POST<ISpecificTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: BIOGRAPHY_DATA,
        variables: {
          id: talentId,
        },
      },
    });

    return response.data;
  };

  const specificTalentSkills = async (talentId: string) => {
    const response = await POST<ISpecificTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: SKILLS_DATA,
        variables: {
          id: talentId,
        },
      },
    });

    return response.data;
  };

  const specificTalentSocial = async (talentId: string) => {
    const response = await POST<ISpecificTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: SOCIAL_DATA,
        variables: {
          id: talentId,
        },
      },
    });

    return response.data;
  };

  const specificTalentWorksheetSizeCard = async (talentId: string) => {
    const response = await POST<ISpecificTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: SPECIFIC_TALENT_WORKSHEET_SIZE_CARD,
        variables: {
          id: talentId,
        },
      },
    });

    return response.data;
  };

  const specificTalentByEmail = async (email: string) => {
    const response = await POST<ISpecificTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: SPECIFIC_TALENT_EMAIL,
        variables: {
          email,
        },
      },
    });

    return response.data;
  };

  const specificTalentProfessions = async (talentId: string) => {
    const response = await POST<ISpecificTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: SPECIFIC_TALENT_PROFESSIONS,
        variables: {
          id: talentId,
        },
      },
    });

    return response.data;
  };

  const updateMe = async (payload: ITalentUpdatePayload) => {
    const response = await PATCH({
      url: `${ENDPOINTS.ME}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };
  const createTalent = async (payload: ITalentCreatePayload) => {
    const response = await POST<ITalentCreateResponse>({
      url: ENDPOINTS.TALENTS,
      data: payload,
    });

    return response.data;
  };

  const getStatistics = async () => {
    const response = await GET<ITalentStatisticsResponsePayload>({
      url: `${ENDPOINTS.TALENTS}/statistics`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getResume = async (talent_id: string) => {
    const response = await GET<ITalentResumeResponsePayload>({
      url: `${ENDPOINTS.TALENTS}/resume?talent_id=${talent_id}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getSkills = async () => {
    const response = await GET<ITalentSkillResponsePayload>({
      url: `${ENDPOINTS.TALENTS}/skills`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getGeneral = async () => {
    const response = await GET<ITalentGeneralResponsePayload>({
      url: `${ENDPOINTS.TALENTS}/general`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getMe = async () => {
    const response = await GET<ITalentMeResponsePayload>({
      url: `${ENDPOINTS.ME}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getTalentProfiles = async (variables: IFilterVariables) => {
    const response = await POST<IProfileResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: GET_TALENT_PROFILES,
        variables,
      },
    });

    return response.data;
  };

  const searchTalentListing = async (payload: ITalentListPayload, isQS?: boolean, customGraphQLResponse?: string) => {
    const response = await POST<ITalentListingResponse>({
      url: ENDPOINTS.GRAPHQL,
      data: {
        query: isQS ? QS_QUERIES : customGraphQLResponse ? customGraphQLResponse : TALENT_LIST_CARD,
        variables: {
          ...payload,
        },
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (response.data.errors) {
      throw new Error('Error' + response.data.errors);
    }

    return response.data;
  };

  const getBiography = async () => {
    const response = await GET<ITalentBiographyResponsePayload>({
      url: `${ENDPOINTS.TALENTS}/biography`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getSizeCard = async (talentId: string) => {
    const response = await POST<ISpecificTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: SIZE_CARD_DATA,
        variables: {
          id: talentId,
        },
      },
    });

    return response.data;
  };

  const getSenderDetails = async (id: string, type: 'Talent' | 'Company') => {
    const response = await GET<ISenderInfoResponsePayload>({
      // url: `${ENDPOINTS.PROFESSION}?type=${type}&id=${id}`,
      url: `${ENDPOINTS.SENDER_INFO}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      params: {
        type,
        id,
      },
    });
    return response.data;
  };

  const updateAgencyTalent = async (id: string, payload: ITalentAgencyUpdateRequestPayload | ITalentUpdatePayload) => {
    // try {
    const response = await PATCH<ITalentAgencyResponsePayload>({
      url: `${ENDPOINTS.TALENTS}/${id}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
    // } catch (e) {
    //   return e;
    // }
  };

  const attachTalentToGuardian = async (payload: IGuardianAttachPayload) => {
    // try {
    const response = await PATCH({
      url: `${ENDPOINTS.GUARDIAN_ATTACH}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  const getTalentList = async (payload: ITalentListRequestPayload, query: string) => {
    const response = await POST<ITalentListResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      data: {
        query: query ? query : TALENT_LIST,
        variables: {
          ...payload,
        },
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (response.data.errors) {
      throw new Error('Error' + response.data.errors);
    }

    return response.data;
  };

  const getTotalTalent = async (payload: Omit<ITalentListRequestPayload, 'first'>) => {
    const response = await POST<ITalentListingResponse>({
      url: ENDPOINTS.GRAPHQL,
      data: {
        query: TOTAL_TALENT_LIST,
        variables: {
          ...payload,
        },
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (response.data.errors) {
      throw new Error('Error' + response.data.errors);
    }

    return response.data;
  };

  const sendTalentInvites = async (payload: ITalentInvitePayload) => {
    const response = await POST<ITalentCreateResponse>({
      url: ENDPOINTS.INVITE_TALENT,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: payload,
    });

    return response.data;
  };

  const claimGhostedProfile = async (profile_ids: string[], medium_id?: string, user_id?: string) => {
    const response = await PATCH<IProfileResponsePayload>({
      url: `${ENDPOINTS.CLAIM_MULTIPLE_PROFILES}`,
      data: {
        profile_ids,
        medium_id,
        user_id,
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const createTalentPalylist = async (payload: ITalentPlaylistPayload) => {
    const response = await POST<ITalentCreateResponse>({
      url: ENDPOINTS.PLAYLIST,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: payload,
    });

    return response.data;
  };

  const getTalentCalendarEvents = async (payload: ITalentCalendarEventsPayload, query?: string) => {
    const response = await POST<ITalentCalendarEventsResponse>({
      url: ENDPOINTS.GRAPHQL,
      data: {
        query: query || TALENT_CALENDAR_EVENTS,
        variables: {
          ...payload,
        },
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (response.data.errors) {
      throw new Error('Error' + response.data.errors);
    }

    return response.data;
  };

  const createCalendarEvent = async (payload: ITalentCalendarEventPayload) => {
    const response = await POST<{ message: string }>({
      url: ENDPOINTS.CALENDAR_EVENTS,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: payload,
    });

    return response.data;
  };

  const updateCalendarEvent = async (payload: ITalentCalendarEventPayload) => {
    const response = await PATCH<{ message: string }>({
      url: `${ENDPOINTS.CALENDAR_EVENTS}/${payload.id}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: payload,
    });

    return response.data;
  };

  const getCalendarAggregates = async (payload: ITalentCalendarEventAggregatesPayload) => {
    const response = await GET<ITalentCalendarEventAggregatesResponse>({
      url: `${ENDPOINTS.CALENDAR_EVENT_AGGREGATES}?date_from=${payload.date_from}&date_to=${
        payload.date_to
      }&talent_id=${payload.talent_id}${
        payload.calendar_event_ids ? `&calendar_event_ids[]=${payload.calendar_event_ids}` : ''
      }${payload.timezone ? `&timezone=${payload.timezone}` : ''}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const deleteCalendarEvent = async (calendarEventId: string, talentId: string) => {
    const response = await DELETE<{ message: string }>({
      url: `${ENDPOINTS.CALENDAR_EVENTS}/${calendarEventId}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        talent_id: talentId,
      },
    });

    return response.data;
  };

  return {
    createTalentPalylist,
    getTalentPlaylist,
    claimGhostedProfile,
    attachTalentToGuardian,
    specificTalentByEmail,
    searchTalentListing,
    getSenderDetails,
    updateMe,
    getMediaTags,
    searchTalent,
    specificTalent,
    getTalentProfiles,
    getMe,
    createTalent,
    getSkills,
    getResume,
    updateTalent,
    getStatistics,
    getBiography,
    getGeneral,
    getSizeCard,
    updateAgencyTalent,
    getTalentList,
    getTotalTalent,
    sendTalentInvites,
    specificTalentGeneralAppearance,
    specificTalentSizeCard,
    specificTalentWorksheetSizeCard,
    specificTalentResume,
    specificTalentBiography,
    specificTalentSkills,
    specificTalentSocial,
    specificTalentProfessions,
    getTalentCalendarEvents,
    createCalendarEvent,
    updateCalendarEvent,
    getCalendarAggregates,
    deleteCalendarEvent,
  };
};
